import { ContentfulThemeApiClient } from '@jbhi-fi/apis/src/contentfulThemeApi';
import { AdsQuery } from '@jbhi-fi/apis/src/contentfulThemeApi/types';
import { useQuery } from 'react-query';
import {
  ContentfulCampaign,
  ContentfulCarouselBanner,
  ContentfulLeaderboardBanner,
  ContentfulPercentOffBanner,
  ContentfulProductBanner,
  ContentfulSupplierBanner,
  ContentfulWingsBanner,
} from '@jbhi-fi/apis/src/contentful/types';
import { ContentfulClient } from '@jbhi-fi/apis/src/contentful/ContentfulClient';

export type UseAds = {
  banners: Array<{
    entry: ContentfulCarouselBanner | ContentfulPercentOffBanner | ContentfulProductBanner | ContentfulSupplierBanner;
    campaignName: string;
    category: string;
  }>;
  wings?: {
    leftWing?: ContentfulWingsBanner;
    rightWing?: ContentfulWingsBanner;
    campaignName?: string;
    campainStartDate?: string;
    category?: string;
  };
  leaderboards: Array<{ entry: ContentfulLeaderboardBanner; category: string }>;
  isLoading: boolean;
  error: unknown;
};

export const sortCampaigns = (campaigns: Array<ContentfulCampaign>): Array<ContentfulCampaign> => {
  return campaigns
    .filter(campaign => campaign.fields?.campaignRulesets)
    .sort((a, b) => {
      const aPriority = a.fields?.campaignRulesets.fields?.carouselPriority;
      const bPriority = b.fields?.campaignRulesets.fields?.carouselPriority;

      const aHasPriority = typeof aPriority === 'string';
      const bHasPriority = typeof bPriority === 'string';

      // Sort entries with priority before entries without priority
      if (aHasPriority && !bHasPriority) {
        return -1;
      }
      if (!aHasPriority && bHasPriority) {
        return 1;
      }

      // If both entries have a priority or both don't, sort by carouselPriority
      if (aHasPriority && bHasPriority && aPriority !== bPriority) {
        return parseInt(aPriority) - parseInt(bPriority);
      }

      // If carouselPriority is the same, sort by updatedAt
      const aUpdatedAt = a.fields ? Date.parse(a.fields.campaignRulesets.sys.updatedAt) : 0;
      const bUpdatedAt = b.fields ? Date.parse(b.fields.campaignRulesets.sys.updatedAt) : 0;
      return bUpdatedAt - aUpdatedAt;
    });
};

export const extractBanners = (campaigns: Array<ContentfulCampaign>, handle: string): UseAds['banners'] => {
  const banners = campaigns.flatMap(
    campaign =>
      campaign?.fields?.carouselBanners?.map(banner => ({
        entry: banner,
        campaignName: campaign?.fields?.name ?? '',
        category: handle,
      })) ?? []
  );
  return [...new Map(banners.map(banner => [banner.entry.sys.id, banner])).values()];
};

export const extractWings = (campaigns: Array<ContentfulCampaign>, handle: string): UseAds['wings'] => {
  const campaignWithWings = campaigns.find(campaign => campaign.fields?.leftWing || campaign.fields?.rightWing);
  return {
    leftWing: campaignWithWings?.fields?.leftWing,
    rightWing: campaignWithWings?.fields?.rightWing,
    campaignName: campaignWithWings?.fields?.name,
    campainStartDate: campaignWithWings?.fields?.campaignRulesets.fields?.startTime,
    category: handle,
  };
};

export const extractLeaderboards = (campaigns: Array<ContentfulCampaign>, handle: string) => {
  return campaigns.flatMap(
    campaign =>
      campaign?.fields?.leaderboards?.map(banner => ({
        entry: banner,
        category: handle,
      })) ?? []
  );
};

export type useContentfulAdsType = {
  queryOptions: AdsQuery['options'];
  contentfulThemeApiClient?: ContentfulThemeApiClient;
  isPagefly?: boolean;
  contentfulClient?: ContentfulClient;
};

export const useContentfulAds = ({
  queryOptions,
  contentfulThemeApiClient,
  isPagefly,
  contentfulClient,
}: useContentfulAdsType): UseAds => {
  if (!contentfulThemeApiClient) {
    throw new Error('useContentfulAds must be called with a contentfulThemeApiClient');
  }
  const isPreview = window.Jbhifi?.isContentPreview || false;
  const parsedQueryOptions = { ...queryOptions, handle: queryOptions?.handle?.split('_')[0] };
  const { data, error, status } = useQuery({
    enabled: Boolean(queryOptions?.handle),
    queryKey: ['useContentfulAds', parsedQueryOptions],
    queryFn: async () =>
      isPreview
        ? contentfulThemeApiClient.getCampaigns(parsedQueryOptions, isPagefly)
        : await contentfulClient?.getCampaigns(parsedQueryOptions, isPagefly),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
  const isLoading = status === 'loading';

  if (error || !data?.data?.items?.length) return { isLoading, banners: [], leaderboards: [], error };

  const sortedCampaigns = sortCampaigns(data.data.items);

  return {
    isLoading,
    banners: extractBanners(sortedCampaigns, queryOptions.handle),
    wings: extractWings(sortedCampaigns, queryOptions.handle),
    leaderboards: extractLeaderboards(sortedCampaigns, queryOptions.handle),
    error,
  };
};
